import { BRIDGE_LATEST_RELEASE_URL } from '../../config/bridgeDownloads';
import { MIN_BRIDGE_VERSION, desktopOsFromUserAgent, openBridgeHint, type LaunchState } from '../../utils/bridgeLaunch';

interface BridgeLaunchNoticeProps {
  state: LaunchState;
  onDismiss: () => void;
  onRecheck: () => void;
  onDownload?: () => void;
  compact?: boolean;
}

export function BridgeLaunchNotice({ state, onDismiss, onRecheck, onDownload, compact = false }: BridgeLaunchNoticeProps) {
  if (state.kind === 'idle') return null;

  if (state.kind === 'waiting' || state.kind === 'rechecking' || state.kind === 'connected') {
    const connected = state.kind === 'connected';
    const text = state.kind === 'waiting'
      ? 'Opening Bridge…'
      : state.kind === 'rechecking' ? 'Checking for Bridge…' : 'Bridge connected';
    return (
      <p
        role="status"
        className={`text-xs ${compact ? '' : 'mx-1 mt-1 px-2.5'} ${
          connected ? 'text-green-600 dark:text-green-400' : 'text-gray-500 dark:text-gray-400'
        }`}
      >
        {text}
      </p>
    );
  }

  const dismissButton = (
    <button
      onClick={onDismiss}
      className="ml-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 underline"
    >
      Dismiss
    </button>
  );

  if (state.kind === 'checkIn') {
    const title = "Bridge hasn't checked in yet";
    const body = `Make sure Bridge is open and you're signed in, and that your music player is open. If Bridge isn't open, ${openBridgeHint(desktopOsFromUserAgent(navigator.userAgent))}.`;
    const checkInActions = (
      <>
        <button
          onClick={onRecheck}
          className="inline-flex items-center gap-1 text-primary hover:text-primary/80 underline font-medium"
        >
          I've done that
        </button>
        {dismissButton}
      </>
    );

    if (compact) {
      return (
        <div role="status" className="text-xs">
          <p className="text-gray-700 dark:text-gray-200 font-medium">{title}</p>
          <p className="text-gray-600 dark:text-gray-400 mb-1">{body}</p>
          {checkInActions}
        </div>
      );
    }

    return (
      <div role="status" className="mx-1 mt-1 p-2.5 rounded-lg bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 text-xs">
        <p className="text-gray-700 dark:text-gray-200 font-medium mb-1">{title}</p>
        <p className="text-gray-600 dark:text-gray-400 mb-2">{body}</p>
        {checkInActions}
      </div>
    );
  }

  const title = state.kind === 'reinstall' ? "Bridge still isn't connecting" : 'Bridge needs an update';
  const body = state.kind === 'reinstall'
    ? "Bridge is open and signed in but isn't reaching VoteBeats. Try uninstalling Bridge, then download and install the latest version."
    : `Bridge ${state.version ?? '(older version)'} is connected, but version ${MIN_BRIDGE_VERSION} or newer is needed. Restart Bridge to let it update, or download the latest.`;

  const links = (
    <>
      <a
        href={BRIDGE_LATEST_RELEASE_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1 text-primary hover:text-primary/80 underline font-medium"
        onClick={() => { onDismiss(); onDownload?.(); }}
      >
        Download Latest
      </a>
      {dismissButton}
    </>
  );

  if (compact) {
    return (
      <div role="alert" className="text-xs text-amber-600 dark:text-amber-400">
        <p className="font-medium">{title}</p>
        <p className="mb-1">{body}</p>
        {links}
      </div>
    );
  }

  return (
    <div role="alert" className="mx-1 mt-1 p-2.5 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-xs">
      <p className="text-amber-700 dark:text-amber-400 font-medium mb-1">{title}</p>
      <p className="text-amber-600 dark:text-amber-500 mb-2">{body}</p>
      {links}
    </div>
  );
}
